import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import InlineAlert from './InlineAlert';
import { useViewport } from '../hooks/useViewport';

interface DesktopRecommendedNoticeProps {
  sx?: SxProps<Theme>;
}

/**
 * Tells a user below `md` (900px: a phone or a portrait tablet) that this editor is built for desktop.
 * Informational only: it hides and blocks nothing, and renders nothing from `md` up.
 */
const DesktopRecommendedNotice: React.FC<DesktopRecommendedNoticeProps> = ({ sx }) => {
  const { isCompact } = useViewport();
  if (!isCompact) return null;
  return (
    <InlineAlert severity="info" sx={sx}>
      This screen is built for desktop. It works here, but a larger screen is easier.
    </InlineAlert>
  );
};

/**
 * Wraps a side-by-side tab body so the notice sits above it below `md`. The wrapper and its parent chain stay
 * the same at every width, so crossing `md` (rotating an iPad) never remounts the editor and loses a draft;
 * from `md` up the wrappers are `display: contents`, so the desktop layout is untouched.
 */
export const DesktopRecommendedArea: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isCompact } = useViewport();
  return (
    <Box
      sx={
        isCompact
          ? { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
          : { display: 'contents' }
      }
    >
      <DesktopRecommendedNotice sx={{ mx: 1, mt: 1 }} />
      <Box sx={isCompact ? { flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' } : { display: 'contents' }}>
        {children}
      </Box>
    </Box>
  );
};

export default DesktopRecommendedNotice;
